import { useSelector } from 'react-redux';

import { useClassroomContext } from 'Context/ClassroomContext';
import { useConfig } from 'Context/ConfigContext';
import { myProfile } from 'Redux/user-profile/selectors';
import { canRedirectToTungKai } from 'Routes/MyCourses/Components/utils.js';
import { useCapacitor } from 'Util/Capacitors';
import { COURSE_MODE } from 'Util/constants';
import { navigateToTungKai } from 'Util/navigateToTungKai';

/**
 * Leaving the classroom of a TungKai course goes back to its TungKai learning page,
 * focused on the current subsection.
 *
 * @returns {() => Promise<boolean>} resolves false when the course can't go to TungKai
 * or the redirect fails, so the caller can fall back to its own exit route
 */
const useGoToTungKaiLearningPage = () => {
  const { courseData, courseId, currentSubsectionId } = useClassroomContext();
  const { openInApp } = useCapacitor();
  const { tungKai, userApi } = useConfig();
  const user = useSelector(myProfile);

  return async () => {
    const { enable, enableWhiteList, whitelist, courseCardRedirectPath } = tungKai || {};
    const canGoToTungKai = canRedirectToTungKai({
      enable,
      enableWhiteList,
      whitelist,
      userId: user?.id,
    });

    if (courseData?.courseMode !== COURSE_MODE.TUNGKAI || !canGoToTungKai) {
      return false;
    }
    // navigateToTungKai only logs (no throw) when these are missing
    if (!tungKai?.loginUrl || !openInApp) {
      return false;
    }

    const redirectPath =
      (courseCardRedirectPath || '/').replace('{courseId}', courseId) +
      `?subsectionId=${currentSubsectionId}`;

    try {
      await navigateToTungKai({
        redirectPath,
        config: { userApi, tungKai },
        openInApp,
      });
      return true;
    } catch {
      // navigateToTungKai already logs the error
      return false;
    }
  };
};

export default useGoToTungKaiLearningPage;
